<script setup lang="ts">
import { ref, computed, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import yup from '@/plugins/yup'
import InputGroup from '@/components/Auths/InputGroup.vue'
import ButtonLoading from '@/components/Buttons/ButtonLoading.vue'
import { ValidationError } from 'yup'
import { useErrorStore } from '@/store/error'
import { InputFromField } from '@/interfaces/components/inputFromField'
import OutlineButton from '@/components/Globals/Buttons/OutlineButton.vue'
import { useAlarmProfileStore } from '@/store/alarmProfile'
import { IDefrostAlarmSensor } from '@/interfaces/entities/alarm_profile'

const props = defineProps<{
  currentProfile: IDefrostAlarmSensor | null,
  formType: 'create' | 'edit',
}>()

const emit = defineEmits(['cancel'])

const { t } = useI18n()
const isLoading = ref(false)
const profileStore = useAlarmProfileStore()

const currentSettings = props.currentProfile?.settings

const inputs: Ref<InputFromField[]> = ref([
  {
    label: 'Nome',
    placeholder: 'alarms.inform_profile_name',
    value: props.formType === 'edit' ? props.currentProfile?.name ?? '' : '',
  },
  {
    label: 'alarms.min_current',
    placeholder: 'alarms.current_message',
    value: props.formType === 'edit' ? currentSettings?.defrost_min_current_setpoint : '',
  },
  {
    label: 'alarms.max_current',
    placeholder: 'alarms.current_message',
    value: props.formType === 'edit' ? currentSettings?.defrost_max_current_setpoint : '',
  },
  {
    label: 'alarms.defrost_time.defrost_current_ratio_setpoint_max',
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType === 'edit' ? currentSettings?.defrost_current_ratio_setpoint_max : '',
  },
  {
    label: 'alarms.defrost_time.defrost_current_ratio_setpoint_min',
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType === 'edit' ? currentSettings?.defrost_current_ratio_setpoint_min : '',
  },
  {
    label: 'alarms.defrost_time.defrost_duration_setpoint',
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType === 'edit' ? currentSettings?.defrost_duration_setpoint : '',
  },
  {
    label: 'alarms.defrost_time.defrost_duration_error_margin',
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType === 'edit' ? currentSettings?.defrost_duration_error_margin : '',
  },
  {
    label: 'alarms.defrost_time.defrost_debounce_duration_setpoint',
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType === 'edit' ? currentSettings?.defrost_debounce_duration_setpoint : '',
  },
])

const alarmSettingsSchema = yup.object({
  name: yup
  .string()
  .required('O campo nome é obrigatório'),

  defrost_min_current_setpoint: yup
    .number()
    .typeError('O campo de corrente mínima deve ser um número')
    .required('O campo de corrente mínima é obrigatório')
    .min(0, 'A corrente mínima não pode ser menor que zero')
    .max(yup.ref('defrost_max_current_setpoint'), 'A corrente mínima não pode ser maior que a máxima'),

  defrost_max_current_setpoint: yup
    .number()
    .typeError('O campo de corrente máxima deve ser um número')
    .required('O campo de corrente máxima é obrigatório')
    .min(0, 'A corrente máxima não pode ser menor que zero'),

  defrost_current_ratio_setpoint_max: yup
    .number()
    .typeError('O campo de relação de corrente deve ser entre 0 e 100')
    .required('O campo de relação de corrente é obrigatório')
    .min(0, 'A relação é no mínimo 0')
    .max(100, 'A relação é no máximo 100'),

  defrost_current_ratio_setpoint_min: yup
    .number()
    .typeError('O campo de relação de corrente deve ser entre 0 e 100')
    .required('O campo de relação de corrente é obrigatório')
    .min(0, 'A relação é no mínimo 0')
    .max(100, 'A relação é no máximo 100'),

  defrost_duration_setpoint: yup
    .number()
    .typeError('O campo de duração de degelo deve ser em minutos')
    .required('O campo de duração de degelo é obrigatório')
    .min(0, 'O tempo é no mínimo 0'),

  defrost_duration_error_margin: yup
    .number()
    .typeError('O campo de margem de erro de duração de degelo deve ser um percentual')
    .required('O campo de margem de erro é obrigatório')
    .min(0, 'A margem é no mínimo 0'),

  defrost_debounce_duration_setpoint: yup
    .number()
    .typeError('O campo de duração de debounce deve ser em minutos')
    .required('O campo de duração de debounce é obrigatório')
    .min(0, 'A duração de debounce é no mínimo 0'),
})


async function handleSubmit() {
  const values: IDefrostAlarmSensor = {
    name: String(inputs.value[0].value),
    defrost_min_current_setpoint: inputs.value[1].value,
    defrost_max_current_setpoint: inputs.value[2].value,
    defrost_current_ratio_setpoint_max: inputs.value[3].value,
    defrost_current_ratio_setpoint_min: inputs.value[4].value,
    defrost_duration_setpoint: inputs.value[5].value,
    defrost_duration_error_margin: inputs.value[6].value,
    defrost_debounce_duration_setpoint: inputs.value[7].value,
  }
  try {
    await alarmSettingsSchema.validate(values, { abortEarly: false })
    isLoading.value = true
    if(props.formType === 'create'){
      await profileStore.createDefrostTimeAlarmProfile(values)
    } else if(props.formType === 'edit' && props.currentProfile){
      await profileStore.editDefrostTimeAlarmProfile(props.currentProfile.id, values)
    }
    isLoading.value = false
    emit('cancel')
  } catch (err) {
    if (err instanceof ValidationError) {
      useErrorStore().clearErrors();
      useErrorStore().setErrors('Erro ao criar perfil: ', err.errors)
    }
  }
}
</script>

<template>
  <div class="border border-stroke bg-gray-2 p-5 shadow-sm">
    <div class="flex flex-wrap gap-4">
      <span v-if="props.formType ==='create'" class="w-full text-base font-medium text-gray-500 mb-1">
        {{ t("alarms.burnt_resistor.create_profile") }}
      </span>
      <div v-if="props.formType === 'edit'" class="w-full flex justify-items-center">
        <span class="w-full text-base font-medium text-gray-500 mb-1">
          {{ "Editar perfil: " + (props.currentProfile?.name || '') }}
        </span>
      </div>
      <div class="w-full border-t border-stroke pt-1"></div>
      <template v-for="(input, index) in inputs" :key="index">
        <div class="min-w-[200px] w-full">
          <InputGroup v-model="input.value" :label="t(input.label)" :placeholder="t(input.placeholder)" />
        </div>
      </template>

      <div class="flex w-full justify-end gap-2">
        <OutlineButton 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !border-stroke !bg-white !shadow-sm hover:!bg-gray-2 !flex !items-center !justify-center whitespace-nowrap"
          @click="emit('cancel')"
          label="Cancelar" />
        <ButtonLoading 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !text-white !border-blue-600 !bg-blue-600 !shadow-sm hover:!bg-blue-700 !flex !items-center !justify-center whitespace-nowrap"
          :isLoading="isLoading"
          @submit="handleSubmit"
          label="Salvar" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  :deep(label) {
    color: #64748b;
    font-size: 0.875rem;
  }
  :deep(input) {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
</style>
